import React, { useEffect, useState } from 'react';
import { multiFactor, reload, signOut } from 'firebase/auth';
import { auth } from '../../firebaseConfig';
import { MFA_LOGIN_NOTICE, mfaError, removeMfaMethod } from '../common/mfa';
import { confirmApp } from '../common/appDialog';
import MfaEnrollment from './MfaEnrollment';
import { requestRecentMfa } from './mfaDialogs';
import { mfaStyles as styles } from './mfaStyles';

export default function MfaSecuritySection({ disabled = false, onBusyChange }: {
    disabled?: boolean;
    onBusyChange: (busy: boolean) => void;
}) {
    const [factors, setFactors] = useState(() => auth.currentUser ? [...multiFactor(auth.currentUser).enrolledFactors] : []);
    const [adding, setAdding] = useState(false);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    useEffect(() => { onBusyChange(busy || adding); }, [busy, adding, onBusyChange]);

    const change = async (action: () => Promise<void>) => {
        setBusy(true);
        setError('');
        setMessage('');
        try {
            await requestRecentMfa();
            await action();
        } catch (failure) {
            setError(mfaError(failure));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (uid: string) => {
        if (!await confirmApp('Remove this verified method? Your remaining method will be required at your next sign-in.', { title: 'Remove security method', tone: 'danger' }))
            return;
        await change(async () => {
            await removeMfaMethod(auth, uid);
            if (auth.currentUser)
                await reload(auth.currentUser);
            setFactors(auth.currentUser ? [...multiFactor(auth.currentUser).enrolledFactors] : []);
            setMessage('Security method removed.');
        });
    };

    return <div className="space-y-4">
        <p className={styles.text}>MFA is required. Add a backup method before replacing a device. Keep backup access separate from your main phone.</p>
        {error && <p role="alert" className={styles.error}>{error}</p>}
        {message && <p role="status" className={styles.notice}>{message}</p>}
        <ul className="space-y-3">{factors.map(factor => <li key={factor.uid} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3"><div><strong className="text-sm">{factor.displayName || (factor.factorId === 'phone' ? 'SMS phone' : 'Authenticator app')}</strong><p className={styles.text}>{factor.factorId === 'phone' && 'phoneNumber' in factor ? String(factor.phoneNumber) : 'Time-based app codes'}</p></div>
            <button className={styles.secondary} disabled={disabled || busy || adding || factors.length < 2} onClick={() => void remove(factor.uid)}>Remove</button></li>)}</ul>
        <p className={styles.text}>Your last method cannot be removed. Lost all methods? <a className="font-semibold text-blue-600 underline" href="mailto:support@eprims.com?subject=MFA%20account%20recovery">Contact support for verified recovery.</a></p>
        {adding ? <MfaEnrollment onCancel={() => setAdding(false)} onComplete={() => {
            sessionStorage.setItem(MFA_LOGIN_NOTICE, 'Security method saved. Sign in again using one of your verified methods.');
            void signOut(auth);
        }} /> : <button className={styles.button} disabled={disabled || busy || factors.length >= 5} onClick={() => void change(async () => { setAdding(true); })}>Add backup or replacement method</button>}
    </div>;
}
